"use client";

import { useListingFacetCounts } from "@/app/(saas)/(app)/businesses/_components/filters/use-listing-facet-counts";
import { DetailsFilter } from "@/app/(saas)/(app)/businesses/_components/filters/details-filter";
import { PriceAndFinancialsFilter } from "@/app/(saas)/(app)/businesses/_components/filters/price-and-financials-filter";
import { PriceChangeFilter, getPriceChangeFilterCount } from "@/app/(saas)/(app)/businesses/_components/filters/price-change-filter";
import { BusinessTypeFilter } from "@/app/(saas)/(app)/businesses/_components/filters/type-filter";
import { SelectedFilterCount } from "@/components/common/selected-filter-count";
import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ListingStatusBadge } from "@/components/listings/listing-status-badge";
import { cn } from "@/lib/utils";
import { useAtom } from "jotai";
import { useTranslations } from "next-intl";
import {
  cloneElement,
  isValidElement,
  useMemo,
  useState,
  type PropsWithChildren,
  type ReactElement,
} from "react";
import {
  PUBLICLY_FILTERABLE_LISTING_STATUSES,
  type PubliclyFilterableListingStatus,
} from "@repo/utils";
import { Check } from "lucide-react";
import { Sheet } from "react-modal-sheet";

type MobileFilterSection =
  | "root"
  | "businessType"
  | "status"
  | "financials"
  | "multiples"
  | "dealTerms"
  | "priceChanges"
  | "details"
  | "financing";

export function BusinessMobileFilters({ children }: PropsWithChildren) {
  const t = useTranslations();

  const [filters, setFilters] = useAtom(businessFiltersAtom);
  const facets = useListingFacetCounts();
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState<MobileFilterSection>("root");

  const detailsFiltersCount = useMemo(() => {
    const nonDefaultRanges = [
      filters.establishmentAgeRange,
      filters.employeeCountRange,
      filters.ownerWorkedHoursRange,
    ].filter((range) => range?.min !== null || range?.max !== null);

    return (
      nonDefaultRanges.length +
      (filters.propertyIncluded ? 1 : 0) +
      filters.listedBy.length
    );
  }, [filters]);

  const financingFiltersCount = useMemo(() => {
    return [
      filters.financingAvailable,
      filters.sbaPrequalified,
      filters.visaQualified,
    ].filter(Boolean).length;
  }, [filters]);

  const financialsFiltersCount = useMemo(() => {
    return [
      filters.priceRange,
      filters.revenueRange,
      filters.profitRange,
      filters.sdeRange,
    ].filter((range) => range?.min !== null || range?.max !== null).length;
  }, [filters]);

  const multiplesFiltersCount = useMemo(() => {
    return [
      filters.sdeMultipleRange,
      filters.revenueMultipleRange,
    ].filter((range) => range?.min !== null || range?.max !== null).length
      + (filters.includeMissingMultiples ? 0 : 1);
  }, [filters]);

  const dealTermsFiltersCount = useMemo(() => {
    return filters.downPaymentRange.min !== null ||
      filters.downPaymentRange.max !== null
      ? 1
      : 0;
  }, [filters.downPaymentRange]);

  const priceChangesFiltersCount = useMemo(
    () => getPriceChangeFilterCount(filters),
    [filters.priceDirection, filters.priceChangedWithinDays, filters.minPriceChangePercent],
  );

  const filtersCount = useMemo(() => {
    return (
      detailsFiltersCount +
      financingFiltersCount +
      financialsFiltersCount +
      multiplesFiltersCount +
      dealTermsFiltersCount +
      priceChangesFiltersCount +
      (filters.businessTypeIds?.length || 0) +
      (filters.statuses?.length || 0)
    );
  }, [
    detailsFiltersCount,
    financingFiltersCount,
    financialsFiltersCount,
    multiplesFiltersCount,
    dealTermsFiltersCount,
    priceChangesFiltersCount,
    filters.businessTypeIds,
    filters.statuses,
  ]);

  function clearAllFilters() {
    setFilters((prev) => ({
      ...prev,
      businessTypeIds: [],
      statuses: [],
      priceRange: { min: null, max: null },
      revenueRange: { min: null, max: null },
      profitRange: { min: null, max: null },
      sdeRange: { min: null, max: null },
      sdeMultipleRange: { min: null, max: null },
      revenueMultipleRange: { min: null, max: null },
      downPaymentRange: { min: null, max: null },
      establishmentAgeRange: { min: null, max: null },
      employeeCountRange: { min: null, max: null },
      ownerWorkedHoursRange: { min: null, max: null },
      propertyIncluded: null,
      listedBy: [],
      financingAvailable: null,
      sbaPrequalified: null,
      visaQualified: null,
      includeMissingMultiples: true,
      priceDirection: null,
      priceChangedWithinDays: null,
      minPriceChangePercent: null,
    }));
  }

  function toggleStatus(status: PubliclyFilterableListingStatus) {
    const isSelected = filters.statuses.includes(status);
    setFilters((prev) => ({
      ...prev,
      statuses: isSelected
        ? prev.statuses.filter((item) => item !== status)
        : [...prev.statuses, status],
    }));
  }

  function toggleFinancing(
    key: "financingAvailable" | "sbaPrequalified" | "visaQualified",
  ) {
    setFilters((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }

  function closeSheet() {
    setOpen(false);
    setSection("root");
  }

  const sheetTitle = useMemo(() => {
    switch (section) {
      case "businessType":
        return t("saas.search.filters.fields.businessTypes.title");
      case "status":
        return t("saas.search.filters.fields.statuses.title");
      case "financials":
        return "Revenue & Profit";
      case "multiples":
        return "Valuation Multiples";
      case "dealTerms":
        return "Deal Terms";
      case "priceChanges":
        return "Price Changes";
      case "details":
        return t("saas.search.filters.fields.details.label");
      case "financing":
        return t("saas.search.filters.fields.financing.label");
      default:
        return t(
          filtersCount
            ? "saas.search.filters.titleCountable"
            : "saas.search.filters.title",
          { count: filtersCount },
        );
    }
  }, [filtersCount, section, t]);

  const totalMatches = facets.data?.total ?? 0;
  const trigger = isValidElement(children)
    ? cloneElement(children as ReactElement<{ onClick?: () => void }>, {
        onClick: () => setOpen(true),
      })
    : null;

  return (
    <>
      {trigger}

      <Sheet isOpen={open} onClose={closeSheet}>
        <Sheet.Backdrop />
        <Sheet.Container className="rounded-t-2xl border-x-0 border-b-0">
          <Sheet.Header />

          <div className="border-b border-border px-4 pb-3 pt-2">
            <div className="flex items-center justify-between gap-3">
              {section === "root" ? (
                <span className="w-9" />
              ) : (
                <button
                  type="button"
                  onClick={() => setSection("root")}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border"
                  aria-label="Back to filters"
                >
                  <Icon icon="ChevronLeft" className="h-4 w-4" />
                </button>
              )}

              <h2 className="truncate text-base font-semibold text-foreground">
                {sheetTitle}
              </h2>

              <button
                type="button"
                onClick={closeSheet}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border"
                aria-label="Close filters"
              >
                <Icon icon="XClose" className="h-4 w-4" />
              </button>
            </div>
          </div>

          <Sheet.Content disableDrag className="h-full">
            <div className="flex h-full flex-col">
              <div className="flex-1 overflow-y-auto pb-24">
                {section === "root" && (
                  <div className="flex flex-col py-2">
                    <SectionRow
                      label={t("saas.search.filters.fields.businessTypes.label")}
                      count={filters.businessTypeIds.length}
                      subtitle={
                        facets.isLoading
                          ? "Loading categories..."
                          : `${Object.keys(facets.data?.businessTypeCounts ?? {}).length} categories available`
                      }
                      onClick={() => setSection("businessType")}
                    />

                    <SectionRow
                      label={t("saas.search.filters.fields.statuses.label")}
                      count={filters.statuses.length}
                      subtitle={
                        facets.isLoading
                          ? "Loading statuses..."
                          : `${PUBLICLY_FILTERABLE_LISTING_STATUSES.filter(
                              (status) => (facets.data?.statusCounts?.[status] ?? 0) > 0,
                            ).length} statuses with results`
                      }
                      onClick={() => setSection("status")}
                    />

                    <SectionRow
                      label="Revenue & Profit"
                      count={financialsFiltersCount}
                      subtitle={
                        financialsFiltersCount > 0
                          ? `${financialsFiltersCount} filters active`
                          : "Filter by the money a business makes and keeps"
                      }
                      onClick={() => setSection("financials")}
                    />

                    <SectionRow
                      label="Valuation Multiples"
                      count={multiplesFiltersCount}
                      subtitle={
                        multiplesFiltersCount > 0
                          ? `${multiplesFiltersCount} filters active`
                          : "How many years of earnings the asking price covers"
                      }
                      onClick={() => setSection("multiples")}
                    />

                    <SectionRow
                      label="Deal Terms"
                      count={dealTermsFiltersCount}
                      subtitle={
                        dealTermsFiltersCount > 0
                          ? `${dealTermsFiltersCount} filters active`
                          : "How much cash you need upfront"
                      }
                      onClick={() => setSection("dealTerms")}
                    />

                    <SectionRow
                      label="Price Changes"
                      count={priceChangesFiltersCount}
                      subtitle={
                        priceChangesFiltersCount > 0
                          ? `${priceChangesFiltersCount} filters active`
                          : "Filter by price drops or increases"
                      }
                      onClick={() => setSection("priceChanges")}
                    />

                    <Accordion type="single" collapsible className="border-t">
                      <AccordionItem value="advanced" className="border-b-0">
                        <AccordionTrigger className="px-5 py-4 text-[15px] font-semibold text-foreground hover:no-underline">
                          <div className="flex items-center gap-2">
                            <span>Advanced filters</span>
                            {(detailsFiltersCount + financingFiltersCount) > 0 && (
                              <SelectedFilterCount
                                count={detailsFiltersCount + financingFiltersCount}
                              />
                            )}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-0">
                          <SectionRow
                            label={t("saas.search.filters.fields.details.label")}
                            count={detailsFiltersCount}
                            subtitle={
                              detailsFiltersCount > 0
                                ? `${detailsFiltersCount} filters active`
                                : "Business age, team size, hours, and property"
                            }
                            onClick={() => setSection("details")}
                          />
                          <SectionRow
                            label={t("saas.search.filters.fields.financing.label")}
                            count={financingFiltersCount}
                            subtitle={
                              facets.isLoading
                                ? "Loading financing options..."
                                : `${[
                                    facets.data?.financingCounts.buyerFinancingAvailable ?? 0,
                                    facets.data?.financingCounts.sbaPrequalified ?? 0,
                                    facets.data?.financingCounts.visaQualified ?? 0,
                                  ].filter((value) => value > 0).length} options with results`
                            }
                            onClick={() => setSection("financing")}
                          />
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                )}

                {section === "businessType" && (
                  <div className="pb-6">
                    <BusinessTypeFilter />
                  </div>
                )}

                {section === "status" && (
                  <div className="px-4 pb-6 pt-4">
                    <div className="grid grid-cols-1 gap-2">
                      {PUBLICLY_FILTERABLE_LISTING_STATUSES.map((status) => {
                        const isSelected = filters.statuses.includes(status);
                        const count = facets.data?.statusCounts?.[status] ?? 0;
                        const disabled = !isSelected && !facets.isLoading && count === 0;

                        return (
                          <button
                            key={status}
                            type="button"
                            onClick={() => toggleStatus(status)}
                            aria-pressed={isSelected}
                            disabled={disabled}
                            className={cn(
                              "flex min-h-11 items-center justify-between rounded-xl border px-3 py-2 text-left transition-colors",
                              isSelected
                                ? "border-foreground bg-muted"
                                : "border-border bg-card hover:border-border",
                              disabled && "cursor-not-allowed opacity-40 hover:border-border",
                            )}
                          >
                            <div className="min-w-0 flex-1">
                              <ListingStatusBadge status={status} small />
                            </div>

                            <div className="ml-3 flex items-center gap-2">
                              <span className="font-data text-xs tabular-nums text-muted-foreground">
                                {facets.isLoading ? "-" : count}
                              </span>
                              <span
                                className={cn(
                                  "inline-flex h-5 w-5 items-center justify-center rounded-md border text-foreground",
                                  isSelected
                                    ? "border-foreground bg-card"
                                    : "border-border bg-card",
                                )}
                                aria-hidden
                              >
                                {isSelected ? <Check className="h-3.5 w-3.5" /> : null}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {section === "financials" && (
                  <div className="px-4 pb-6 pt-4">
                    <PriceAndFinancialsFilter mode="financials" />
                  </div>
                )}

                {section === "multiples" && (
                  <div className="px-4 pb-6 pt-4">
                    <PriceAndFinancialsFilter mode="multiples" />
                  </div>
                )}

                {section === "dealTerms" && (
                  <div className="px-4 pb-6 pt-4">
                    <PriceAndFinancialsFilter mode="dealTerms" />
                  </div>
                )}

                {section === "priceChanges" && (
                  <div className="px-4 pb-6 pt-4">
                    <PriceChangeFilter />
                  </div>
                )}

                {section === "details" && (
                  <div className="px-4 pb-6 pt-4">
                    <DetailsFilter />
                  </div>
                )}

                {section === "financing" && (
                  <div className="px-4 pb-6 pt-4">
                    <div className="mb-3 text-xs text-muted-foreground">
                      Disabled options currently have no matching results in this map area.
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <ChipToggle
                        label={t(
                          "saas.search.filters.fields.financing.financingAvailable.label",
                        )}
                        count={facets.data?.financingCounts.buyerFinancingAvailable ?? 0}
                        active={Boolean(filters.financingAvailable)}
                        disabled={
                          !filters.financingAvailable &&
                          !facets.isLoading &&
                          (facets.data?.financingCounts.buyerFinancingAvailable ?? 0) === 0
                        }
                        onToggle={() => toggleFinancing("financingAvailable")}
                      />
                      <ChipToggle
                        label={t("saas.search.filters.fields.financing.sbaPrequalified.label")}
                        count={facets.data?.financingCounts.sbaPrequalified ?? 0}
                        active={Boolean(filters.sbaPrequalified)}
                        disabled={
                          !filters.sbaPrequalified &&
                          !facets.isLoading &&
                          (facets.data?.financingCounts.sbaPrequalified ?? 0) === 0
                        }
                        onToggle={() => toggleFinancing("sbaPrequalified")}
                      />
                      <ChipToggle
                        label={t("saas.search.filters.fields.financing.visaQualified.label")}
                        count={facets.data?.financingCounts.visaQualified ?? 0}
                        active={Boolean(filters.visaQualified)}
                        disabled={
                          !filters.visaQualified &&
                          !facets.isLoading &&
                          (facets.data?.financingCounts.visaQualified ?? 0) === 0
                        }
                        onToggle={() => toggleFinancing("visaQualified")}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-border bg-card px-4 py-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    className="h-11 shrink-0"
                    onClick={clearAllFilters}
                  >
                    {t("saas.search.filters.clear")}
                  </Button>
                  <Button className="h-11 flex-1" onClick={closeSheet}>
                    Show{" "}
                    <span className="font-data tabular-nums">
                      {facets.isLoading ? "..." : totalMatches}
                    </span>{" "}
                    Businesses
                  </Button>
                </div>
              </div>
            </div>
          </Sheet.Content>
        </Sheet.Container>
      </Sheet>
    </>
  );
}

function SectionRow({
  label,
  count,
  subtitle,
  onClick,
}: {
  label: string;
  count: number;
  subtitle?: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-14 w-full items-center justify-between gap-4 border-t px-5 py-2 text-left"
    >
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          {count > 0 && <SelectedFilterCount count={count} />}
          <span className="truncate text-[15px] font-medium text-foreground">{label}</span>
        </div>
        {subtitle ? (
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      <Icon icon="ChevronRight" className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}

function ChipToggle({
  label,
  count,
  active,
  disabled,
  onToggle,
}: {
  label: string;
  count: number;
  active: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      disabled={disabled}
      className={cn(
        "flex min-h-11 items-center justify-between rounded-xl border px-3 py-2 text-left transition-colors",
        active
          ? "border-foreground bg-muted"
          : "border-border bg-card hover:border-border",
        disabled && "cursor-not-allowed opacity-40 hover:border-border",
      )}
    >
      <span className={cn("text-sm text-foreground", active && "font-semibold")}>{label}</span>
      <span className="ml-3 inline-flex items-center gap-2">
        <span className="font-data text-xs tabular-nums text-muted-foreground">{count}</span>
        <span
          className={cn(
            "inline-flex h-5 w-5 items-center justify-center rounded-md border text-foreground",
            active ? "border-foreground bg-card" : "border-border bg-card",
          )}
          aria-hidden
        >
          {active ? <Check className="h-3.5 w-3.5" /> : null}
        </span>
      </span>
    </button>
  );
}
